import React from 'react'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { Box } from '@hub/box'
import classNames from 'classnames'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import { getCentrePageTemplate } from '../../../lib/page-templates/fetching'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../types/next'
import {
  SGPageTemplateWithAdZones,
  SGPageTemplateBasicContent,
} from '@scentregroup/shared/types/page-templates'
import { makeApolloClient } from '../../../lib/apolloClient'
import BasicContent from '../../../components/page-templates/modules/basic-content'
import { OpeningHours } from '../../../components/page-templates/content-detail/opening-hours-content-details'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { Template } from '@scentregroup/shared/components/page-templates/template'
import { getCentreTradingHoursStartingNow } from '../../../lib/get-trading-hours'
import { MIN_DAYS_TO_SHOW } from '@scentregroup/shared/constants'
import { shouldRedirectForMismatchedRoute } from '@scentregroup/shared/lib/redirects'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/helpers/fetch-header-static-data'

interface OpeningHoursPageProps {
  country: 'Australia' | 'New Zealand'
  topStripProps: ITopStripProps | null
  headerStaticData: CentreStaticData
  centre: CentreDetails
  template: SGPageTemplateWithAdZones
  centreTradingHours: TradingHourDay[]
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
    id: string
    slug: string
  }>
): Promise<GetServerSidePropsResult<OpeningHoursPageProps>> {
  setCacheHeaders(ctx.res)
  const centreSlug = ctx.params?.centre
  if (!centreSlug) {
    return {
      notFound: true,
    }
  }
  const apollo = makeApolloClient()
  const country = localeToCountry(ctx.locale)
  const centre = await getCentreDetails(apollo, { slug: centreSlug, country })

  if (!centre) {
    return {
      notFound: true,
    }
  }
  const redirect = shouldRedirectForMismatchedRoute(
    '/[centre]/opening-hours',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (redirect) {
    return redirect
  }

  const [template, headerStaticData, topStripProps] = await Promise.all([
    getCentrePageTemplate(apollo, centreSlug, 'opening-hours'),
    fetchCentreStaticData(apollo, {
      country,
      centre,
    }),
    getTopStripProps(apollo, country, centreSlug),
  ])

  if (!template) {
    return {
      notFound: true,
    }
  }

  const twoWeeksInDays = 14
  const centreTradingHours = await getCentreTradingHoursStartingNow(
    apollo,
    centre,
    twoWeeksInDays
  )
  return {
    props: {
      country,
      topStripProps,
      centre,
      template,
      headerStaticData,
      centreTradingHours,
    },
  }
}

const OpeningHoursPage: React.FC<
  React.PropsWithChildren<OpeningHoursPageProps>
> = props => {
  const {
    country,
    topStripProps,
    centre,
    template,
    headerStaticData,
    centreTradingHours,
  } = props
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const metaData = {
    title: `Westfield ${centre.title} Opening Hours | Centre Trading Hours`,
    description: `Westfield ${centre.title} - View opening hours for regular business days. Check special or extended trading hours for public holidays at Westfield ${centre.title}.`,
    canonical,
  }

  const basicContentModule: SGPageTemplateBasicContent | undefined =
    template.modules.find(
      (item): item is SGPageTemplateBasicContent =>
        item.__typename === 'SGPageTemplateBasicContent'
    )

  const templateWithoutBasicContent = {
    ...template,
    modules: template.modules.filter(
      item => item.__typename !== 'SGPageTemplateBasicContent'
    ),
  }
  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  return (
    <Layout
      country={country}
      topStrip={topStripProps}
      centre={{
        ...centre,
        contactLinks,
      }}
      header={{
        variant: 'centre',
        data: headerStaticData,
      }}
      metaData={metaData}
    >
      <OpeningHours
        centre={{
          ...centre,
          contactLinks,
        }}
        centreTradingHours={centreTradingHours}
        minDays={MIN_DAYS_TO_SHOW}
      >
        {basicContentModule && (
          <Box
            sx={{
              marginBottom: 'spacing-md',
            }}
          >
            <BasicContent
              disableContainerPadding
              leftAlign
              module={basicContentModule}
            />
          </Box>
        )}
      </OpeningHours>
      <div className={classNames('sc-slot', 'sc-slot-sub-opening')} />
      <Template template={templateWithoutBasicContent} />
    </Layout>
  )
}
export default OpeningHoursPage
